// ─── Focused-deal comp tables (Map "Comps ↗" fullscreen modal) ─────────────
// Three stacked tables for the deal currently focused on the map: Sale Comps,
// Lease Comps, Rent Roll. Each section has the row data plus aggregate stats
// at the bottom (count, avg/median cap rate, avg $/SF, etc.).
//
// Action bar at the top supports:
//   📥 Export  — dumps all three tables to an XLSX file (one sheet per table,
//                plus an aggregate footer row).
//   ✏ Edit    — toggle: when on, every editable cell renders as an inline
//                input. Blur/Enter commits the edit to Supabase + the parent's
//                focusedDealsData state. Escape cancels.
//
// Used during IC meetings to talk through the comp set live on the map AND
// to fix bad data without leaving the modal.

import { useState, useEffect } from "react";
import * as XLSX from "xlsx-js-style";
import { supabase } from "../../supabase";
import { drivingTimes } from "../../services/googleMaps";

// ─── Brand palette (from PP Brand Guidelines V2.0) ────────────────────────
// 6-char RGB hex — xlsx-js-style expects { rgb: "RRGGBB" } (no alpha).
const PP = {
  red:        "E93529",   // primary accent
  redDark:    "C42D22",   // deeper red for hover/active surfaces
  dark:       "333333",   // primary text
  light:      "F4F4F4",   // page background / row stripe
  gray:       "D8D8D8",   // borders / dividers
  white:      "FFFFFFFF", // (xlsx-js-style accepts both forms)
  whiteRGB:   "FFFFFF",
  mutedText:  "6B7280",
};

function avgOf(nums) {
  const xs = nums.filter(n => Number.isFinite(n) && n > 0);
  return xs.length > 0 ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}
function medianOf(nums) {
  const xs = nums.filter(n => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}
function fmtMoney(n) {
  if (n == null || !Number.isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtSF(n) { return n ? `${Number(n).toLocaleString()}` : "—"; }
function fmtPct(n) { return n != null && Number.isFinite(n) ? `${Number(n).toFixed(2)}%` : "—"; }
function fmtRate(n) { return n != null && n > 0 ? `$${Number(n).toFixed(2)}` : "—"; }
function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { year: "2-digit", month: "numeric", day: "numeric" }); }
  catch { return d; }
}

// Coerce a raw <input> value back into a typed DB value. Empty → null so
// "clear this cell" works.
function coerceValue(raw, type) {
  if (raw === "" || raw == null) return null;
  if (type === "number") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  }
  return raw;
}

// Cell renderer. Plain text when not in edit mode (or when the column is
// flagged notEditable). Otherwise an uncontrolled <input> committing on
// blur / Enter, resetting on Escape.
function Cell({ row, col, editMode, onSave, S }) {
  const display = col.render ? col.render(row) : (row[col.key] ?? "—");

  if (!editMode || col.notEditable) {
    return (
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: col.wrap ? "normal" : "nowrap",
          color: col.muted ? S.muted : S.text,
          fontWeight: col.bold ? 700 : 400,
          paddingRight: 6,
        }}
        title={typeof display === "string" ? display : undefined}
      >
        {display}
      </span>
    );
  }

  const raw = row[col.key];
  const initial = raw == null ? "" : String(raw);
  const inputType = col.type === "date" ? "date" : col.type === "number" ? "number" : "text";

  return (
    <input
      defaultValue={initial}
      type={inputType}
      step={col.type === "number" ? "any" : undefined}
      onBlur={(e) => {
        const next = coerceValue(e.target.value, col.type);
        const prev = raw == null ? null : (col.type === "number" ? Number(raw) : raw);
        if (next !== prev && !(next == null && prev == null)) {
          onSave({ rowId: row.id, field: col.key, value: next, type: col.type });
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { e.currentTarget.value = initial; e.currentTarget.blur(); }
      }}
      style={{
        width: "100%",
        background: S.card2,
        border: `1px solid ${S.border}`,
        borderRadius: 3,
        padding: "2px 4px",
        fontSize: 10,
        color: S.text,
        fontFamily: "'Montserrat', sans-serif",
        boxSizing: "border-box",
        outline: "none",
      }}
    />
  );
}

function FooterStat({ label, value, S, accent = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "4px 8px", borderRight: `1px solid ${S.border}`, minWidth: 70 }}>
      <span style={{ fontSize: 8, color: S.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 800, color: accent ? S.red : S.text, marginTop: 1 }}>{value}</span>
    </div>
  );
}

function CompTable({ title, count, columns, rows, footer, S, minWidth = 720, emptyMsg, editMode, onCellSave }) {
  const gridCols = columns.map(c => c.width || "1fr").join(" ");
  return (
    <div style={{ marginBottom: 14, background: S.card, border: `1px solid ${S.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "8px 10px", borderBottom: `1px solid ${S.border}`, background: S.card2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: S.text, textTransform: "uppercase", letterSpacing: 0.6 }}>{title}</span>
        <span style={{ fontSize: 9, color: S.muted, fontWeight: 700 }}>{count} {count === 1 ? "comp" : "comps"}</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 14, textAlign: "center", color: S.muted, fontSize: 10 }}>{emptyMsg || "No data."}</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth }}>
            <div style={{ display: "grid", gridTemplateColumns: gridCols, padding: "6px 10px", borderBottom: `1px solid ${S.border}`, fontSize: 8, fontWeight: 800, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5, background: S.card2 }}>
              {columns.map(c => <span key={c.key}>{c.label}</span>)}
            </div>
            {rows.map((r, i) => (
              <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: gridCols, padding: "6px 10px", borderBottom: i < rows.length - 1 ? `1px solid ${S.border}` : "none", fontSize: 10, color: S.text, alignItems: "center", gap: 4 }}>
                {columns.map(c => (
                  <Cell key={c.key} row={r} col={c} editMode={editMode} onSave={onCellSave} S={S} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {rows.length > 0 && footer && footer.length > 0 && (
        <div style={{ display: "flex", borderTop: `1px solid ${S.border}`, background: S.card2, overflowX: "auto" }}>
          {footer.map((f, i) => <FooterStat key={i} label={f.label} value={f.value} S={S} accent={f.accent} />)}
        </div>
      )}
    </div>
  );
}

export default function FocusedDealCompTables({
  focusedDealIds,
  focusedDealsData,
  setFocusedDealsData,   // optional — if provided, inline edits update the parent's map
  drawerDealId,
  setDrawerDealId,
  S,
}) {
  const [editMode, setEditMode] = useState(false);
  const [busy, setBusy] = useState(false);
  // Distance / drive-time from the currently-shown deal to each comp+RR row.
  // Keyed by comp/rr id → { distanceText, durationText, durationSec }.
  const [driveTimes, setDriveTimes] = useState({});
  const [driveLoading, setDriveLoading] = useState(false);

  // Pick which deal's data to show.
  const targetIds = drawerDealId && focusedDealIds.includes(drawerDealId)
    ? [drawerDealId]
    : focusedDealIds;

  // Fetch driving distances from the deal center to every comp / rent-roll
  // row via Google's Distance Matrix API. Only runs when exactly one deal is
  // focused (multi-deal "distance from" is ambiguous). Results are cached
  // by row id, and we skip anything we've already fetched.
  useEffect(() => {
    if (targetIds.length !== 1) return;
    const dealId = targetIds[0];
    const deal = focusedDealsData.get(dealId);
    if (!deal?.center) return;
    const gatherIds = (rows) => rows
      .filter(r => r.id && Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)))
      .filter(r => !driveTimes[r.id])
      .map(r => ({ id: r.id, lat: Number(r.latitude), lng: Number(r.longitude) }));
    const compsToFetch = gatherIds([...(deal.comps || [])]);
    if (compsToFetch.length === 0) return;
    let cancelled = false;
    setDriveLoading(true);
    (async () => {
      try {
        const results = await drivingTimes(
          deal.center,
          compsToFetch,
          { keyFn: (d) => d.id },
        );
        if (cancelled) return;
        setDriveTimes(prev => ({ ...prev, ...results }));
      } catch (e) {
        console.warn("drivingTimes failed:", e);
      } finally {
        if (!cancelled) setDriveLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIds.join("|"), focusedDealsData]);

  if (focusedDealIds.length === 0) {
    return (
      <div style={{ padding: 18, textAlign: "center", color: S.muted, fontSize: 11, lineHeight: 1.5 }}>
        Focus on a deal first.<br/>
        Use the "View on Map" button from any deal, or click <strong>+ Add deal</strong> above the map.
      </div>
    );
  }

  // Aggregate across the visible deal(s). Carry _dealId so we know which
  // deal's slice to update when an edit fires.
  const allComps = [];
  const allRR = [];
  for (const id of targetIds) {
    const d = focusedDealsData.get(id);
    if (!d) continue;
    for (const c of (d.comps || [])) allComps.push({ ...c, _dealId: id, _dealName: d.name });
    for (const r of (d.rentRoll || [])) allRR.push({ ...r, _dealId: id, _dealName: d.name });
  }
  const sales  = allComps.filter(c => c.type === "sale");
  const leases = allComps.filter(c => c.type === "lease" && c.source !== "rent_roll");

  // ── Aggregate stats ────────────────────────────────────────────────
  const salePsfs = sales.map(c => Number(c.price_psf));
  const saleCaps = sales.map(c => Number(c.cap_rate));
  const saleSizes = sales.map(c => Number(c.building_sf));
  const salePrices = sales.map(c => Number(c.sale_price));
  const avgPsf = avgOf(salePsfs);
  const avgCap = avgOf(saleCaps);
  const medCap = medianOf(saleCaps);
  const totalSaleSize = saleSizes.filter(n => Number.isFinite(n) && n > 0).reduce((a, b) => a + b, 0);
  const totalSalePrice = salePrices.filter(n => Number.isFinite(n) && n > 0).reduce((a, b) => a + b, 0);

  const leaseRates = leases.map(c => Number(c.lease_rate));
  const avgLeaseRate = avgOf(leaseRates);
  const medLeaseRate = medianOf(leaseRates);
  const leaseSizes = leases.map(c => Number(c.suite_sf || c.building_sf));
  const totalLeaseSize = leaseSizes.filter(n => Number.isFinite(n) && n > 0).reduce((a, b) => a + b, 0);

  const rrTotalSf = allRR.reduce((s, r) => s + (Number(r.suite_sf) || 0), 0);
  const rrWeighted = allRR.reduce((s, r) => s + (Number(r.suite_sf) || 0) * (Number(r.lease_rate) || 0), 0);
  const inPlaceWtd = rrTotalSf > 0 ? rrWeighted / rrTotalSf : null;
  const avgWalt = avgOf(allRR.map(r => Number(r.walt_years)));
  const mtm = (avgLeaseRate && inPlaceWtd) ? ((avgLeaseRate / inPlaceWtd) - 1) * 100 : null;

  // ── Column configs (each editable column carries a `type` so the input
  //    renders correctly and the value coerces back to the right shape).
  // Drive-time column render — resolves the deal→comp drivingTimes entry
  // and shows "12 min · 5 mi" or "—" if we don't have coords for this row.
  const renderDrive = (r) => {
    if (targetIds.length !== 1) return "—";
    const dt = driveTimes[r.id];
    if (!dt) return driveLoading ? "…" : "—";
    return dt.durationText ? `${dt.durationText}${dt.distanceText ? ` · ${dt.distanceText}` : ""}` : "—";
  };

  const saleColumns = [
    { key: "property_name", label: "Property", width: "1.3fr", bold: true, type: "text",   render: r => r.property_name || r.address?.split(",")[0] || "—" },
    { key: "address",       label: "Address",  width: "1.4fr", muted: true, type: "text",  render: r => r.address || "—" },
    { key: "_drive",        label: "Drive",    width: "0.9fr", muted: true, notEditable: true, render: renderDrive },
    { key: "comp_date",     label: "Date",     width: "0.8fr", type: "date",   render: r => fmtDate(r.comp_date) },
    { key: "building_sf",   label: "SF",       width: "0.7fr", type: "number", render: r => fmtSF(r.building_sf) },
    { key: "sale_price",    label: "Sale $",   width: "0.8fr", type: "number", render: r => fmtMoney(r.sale_price) },
    { key: "price_psf",     label: "$/SF",     width: "0.6fr", bold: true, type: "number", render: r => r.price_psf ? `$${Number(r.price_psf).toFixed(0)}` : "—" },
    { key: "cap_rate",      label: "Cap",      width: "0.6fr", bold: true, type: "number", render: r => fmtPct(r.cap_rate) },
    { key: "buyer",         label: "Buyer",    width: "1fr",   type: "text",   render: r => r.buyer || "—" },
    { key: "seller",        label: "Seller",   width: "1fr",   type: "text",   render: r => r.seller || "—" },
    { key: "notes",         label: "Notes",    width: "2fr",   wrap: true, muted: true, type: "text", render: r => r.notes || "—" },
  ];

  const leaseColumns = [
    { key: "property_name", label: "Property", width: "1.3fr", bold: true, type: "text",   render: r => r.property_name || r.address?.split(",")[0] || "—" },
    { key: "address",       label: "Address",  width: "1.4fr", muted: true, type: "text",  render: r => r.address || "—" },
    { key: "_drive",        label: "Drive",    width: "0.9fr", muted: true, notEditable: true, render: renderDrive },
    { key: "tenant",        label: "Tenant",   width: "1fr",   type: "text",   render: r => r.tenant || "—" },
    { key: "comp_date",     label: "Date",     width: "0.8fr", type: "date",   render: r => fmtDate(r.comp_date) },
    { key: "suite_sf",      label: "SF",       width: "0.7fr", type: "number", render: r => fmtSF(r.suite_sf || r.building_sf) },
    { key: "lease_rate",    label: "Rate",     width: "0.7fr", bold: true, type: "number", render: r => fmtRate(r.lease_rate) },
    { key: "lease_type",    label: "Type",     width: "0.6fr", type: "text",   render: r => r.lease_type || "—" },
    { key: "landlord",      label: "Landlord", width: "1fr",   type: "text",   render: r => r.landlord || "—" },
    { key: "notes",         label: "Notes",    width: "2fr",   wrap: true, muted: true, type: "text", render: r => r.notes || "—" },
  ];

  const rrColumns = [
    { key: "tenant",     label: "Tenant",   width: "1.3fr", bold: true, type: "text",   render: r => r.tenant || "—" },
    { key: "suite_id",   label: "Suite",    width: "0.6fr", muted: true, type: "text",  render: r => r.suite_id || "—" },
    { key: "suite_sf",   label: "SF",       width: "0.7fr", type: "number", render: r => fmtSF(r.suite_sf) },
    { key: "lease_rate", label: "Rate",     width: "0.7fr", bold: true, type: "number", render: r => fmtRate(r.lease_rate) },
    { key: "lease_type", label: "Type",     width: "0.6fr", type: "text",   render: r => r.lease_type || "—" },
    { key: "lease_start",label: "Start",    width: "0.8fr", type: "date",   render: r => fmtDate(r.lease_start) },
    { key: "lease_end",  label: "End",      width: "0.8fr", type: "date",   render: r => fmtDate(r.lease_end) },
    { key: "walt_years", label: "WALT",     width: "0.6fr", type: "number", render: r => r.walt_years ? `${Number(r.walt_years).toFixed(1)}y` : "—" },
    { key: "notes",      label: "Notes",    width: "2fr",   wrap: true, muted: true, type: "text", render: r => r.notes || "—" },
  ];

  // ── Save handlers ─────────────────────────────────────────────────
  // Writes to the global `comps` table (sale/lease) or `deal_rent_roll`
  // (rent roll). On success, mutates the parent's focusedDealsData so the
  // UI reflects the change without a round-trip refetch.
  async function saveCompCell({ rowId, field, value }) {
    setBusy(true);
    const { error } = await supabase.from("comps").update({ [field]: value }).eq("id", rowId);
    setBusy(false);
    if (error) { window.alert(`Couldn't save: ${error.message}`); return; }
    if (!setFocusedDealsData) return;
    setFocusedDealsData(prev => {
      const next = new Map(prev);
      for (const [did, entry] of next.entries()) {
        if (!entry?.comps?.some(c => c.id === rowId)) continue;
        next.set(did, {
          ...entry,
          comps: entry.comps.map(c => c.id === rowId ? { ...c, [field]: value } : c),
        });
      }
      return next;
    });
  }

  async function saveRentRollCell({ rowId, field, value }) {
    setBusy(true);
    const { error } = await supabase.from("deal_rent_roll").update({ [field]: value }).eq("id", rowId);
    setBusy(false);
    if (error) { window.alert(`Couldn't save: ${error.message}`); return; }
    if (!setFocusedDealsData) return;
    setFocusedDealsData(prev => {
      const next = new Map(prev);
      for (const [did, entry] of next.entries()) {
        if (!entry?.rentRoll?.some(r => r.id === rowId)) continue;
        next.set(did, {
          ...entry,
          rentRoll: entry.rentRoll.map(r => r.id === rowId ? { ...r, [field]: value } : r),
        });
      }
      return next;
    });
  }

  // ── Export to branded XLSX ────────────────────────────────────────
  // Built with xlsx-js-style (drop-in SheetJS fork that keeps cell
  // styling — vanilla `xlsx` strips it). PP red banners, white-on-red
  // column headers, alternating row stripes, bold totals row, frozen
  // top rows, per-column number formats, gray borders. One sheet per
  // table. Numeric columns are written as numbers so Excel/Sheets can
  // sum/avg/sort them natively.
  function exportXLSX() {
    const wb = XLSX.utils.book_new();
    wb.Props = { Title: "Deal Comp Tables", Author: "ProximityParks", Company: "ProximityParks", CreatedDate: new Date() };

    const dealLabel = targetIds.length === 1
      ? (focusedDealsData.get(targetIds[0])?.name || "deal")
      : `${targetIds.length} deals`;
    const stamp = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    // Per-column Excel number format
    const numfmt = (col) => {
      if (col.type !== "number") return null;
      if (col.key === "sale_price")  return '"$"#,##0';
      if (col.key === "price_psf")   return '"$"#,##0';
      if (col.key === "cap_rate")    return '0.00"%"';
      if (col.key === "lease_rate")  return '"$"#,##0.00';
      if (col.key === "walt_years")  return '0.0"y"';
      return '#,##0';
    };

    // Style presets (xlsx-js-style format)
    const borderHeader = { top: { style: "thin", color: { rgb: PP.redDark } }, bottom: { style: "thin", color: { rgb: PP.redDark } }, left: { style: "thin", color: { rgb: PP.redDark } }, right: { style: "thin", color: { rgb: PP.redDark } } };
    const borderBody   = { bottom: { style: "thin", color: { rgb: PP.gray } } };
    const borderTotals = { top: { style: "medium", color: { rgb: PP.dark } }, bottom: { style: "thin", color: { rgb: PP.gray } } };

    const styleBanner = {
      font: { name: "Montserrat", bold: true, sz: 16, color: { rgb: PP.whiteRGB } },
      fill: { patternType: "solid", fgColor: { rgb: PP.red } },
      alignment: { vertical: "center", horizontal: "left", indent: 1 },
    };
    const styleSubtitle = {
      font: { name: "Montserrat", sz: 10, color: { rgb: PP.whiteRGB } },
      fill: { patternType: "solid", fgColor: { rgb: PP.dark } },
      alignment: { vertical: "center", horizontal: "left", indent: 1 },
    };
    const styleSection = {
      font: { name: "Montserrat", bold: true, sz: 11, color: { rgb: PP.red } },
      fill: { patternType: "solid", fgColor: { rgb: PP.light } },
      alignment: { vertical: "center", horizontal: "left", indent: 1 },
    };
    const styleColHeader = {
      font: { name: "Montserrat", bold: true, sz: 9, color: { rgb: PP.whiteRGB } },
      fill: { patternType: "solid", fgColor: { rgb: PP.red } },
      alignment: { vertical: "center", horizontal: "left" },
      border: borderHeader,
    };

    function cellAddr(r, c) { return XLSX.utils.encode_cell({ r, c }); }

    function buildSheet({ title, columns, rows, totals }) {
      const ws = {};
      const nCols = columns.length;
      const lastColIdx = nCols - 1;

      // Row 0 — brand banner
      ws[cellAddr(0, 0)] = { v: "ProximityParks  ·  Deal Comp Tables", t: "s", s: styleBanner };
      // Row 1 — subtitle
      ws[cellAddr(1, 0)] = { v: `${dealLabel}  ·  Exported ${stamp}`, t: "s", s: styleSubtitle };
      // Row 2 — spacer (left blank; height controlled below)
      // Row 3 — section title
      ws[cellAddr(3, 0)] = { v: title.toUpperCase(), t: "s", s: styleSection };
      // Row 4 — column headers
      columns.forEach((c, i) => {
        ws[cellAddr(4, i)] = { v: c.label.toUpperCase(), t: "s", s: styleColHeader };
      });

      // Rows 5+ — data
      const dataStartRow = 5;
      rows.forEach((r, rowIdx) => {
        const stripe = rowIdx % 2 === 1;
        columns.forEach((c, i) => {
          const raw = r[c.key];
          const cellAddress = cellAddr(dataStartRow + rowIdx, i);
          const fontColor = c.muted ? PP.mutedText : PP.dark;
          const style = {
            font: { name: "Montserrat", sz: 10, color: { rgb: fontColor }, bold: !!c.bold },
            alignment: { vertical: "center", horizontal: c.type === "number" ? "right" : "left", wrapText: !!c.wrap },
            border: borderBody,
          };
          if (stripe) style.fill = { patternType: "solid", fgColor: { rgb: PP.light } };
          const fmt = numfmt(c);
          if (fmt) style.numFmt = fmt;

          if (raw == null || raw === "") {
            ws[cellAddress] = { v: "", t: "s", s: style };
          } else if (c.type === "number") {
            const n = Number(raw);
            ws[cellAddress] = Number.isFinite(n) ? { v: n, t: "n", s: style } : { v: String(raw), t: "s", s: style };
          } else {
            ws[cellAddress] = { v: String(raw), t: "s", s: style };
          }
        });
      });

      // Totals row
      let lastUsedRow = dataStartRow + rows.length - 1;
      if (rows.length > 0 && totals && totals.some(t => t.value !== "")) {
        const totalsRowIdx = dataStartRow + rows.length + 1;   // one blank row gap
        totals.forEach((t, i) => {
          if (t.value === "" && !(i === 0 && totals[1]?.value === "")) return;
          const isMergedLabel = (i === 0 && totals[1]?.value === "");
          const value = isMergedLabel ? "TOTALS" : t.value;
          const style = {
            font: { name: "Montserrat", bold: true, sz: 10, color: { rgb: t.accent ? PP.red : PP.dark } },
            fill: { patternType: "solid", fgColor: { rgb: PP.light } },
            alignment: { vertical: "center", horizontal: isMergedLabel ? "left" : "right", indent: isMergedLabel ? 1 : 0 },
            border: borderTotals,
          };
          if (t.numFmt) style.numFmt = t.numFmt;
          const isNumber = typeof value === "number";
          ws[cellAddr(totalsRowIdx, i)] = { v: value, t: isNumber ? "n" : "s", s: style };
        });

        // Merge A:B for the TOTALS label if first two were empty
        if (totals[0].value === "" && totals[1]?.value === "") {
          ws["!merges"] = ws["!merges"] || [];
          ws["!merges"].push({ s: { r: totalsRowIdx, c: 0 }, e: { r: totalsRowIdx, c: 1 } });
        }
        lastUsedRow = totalsRowIdx;
      }

      // Banner / subtitle / section merges across all columns
      ws["!merges"] = ws["!merges"] || [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastColIdx } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: lastColIdx } });
      ws["!merges"].push({ s: { r: 3, c: 0 }, e: { r: 3, c: lastColIdx } });

      // Row heights
      ws["!rows"] = [
        { hpt: 32 },  // banner
        { hpt: 18 },  // subtitle
        { hpt: 8 },   // spacer
        { hpt: 22 },  // section
        { hpt: 22 },  // headers
      ];

      // Column widths (auto-ish, clamped 10..48)
      ws["!cols"] = columns.map(c => {
        const headerLen = c.label.length + 2;
        const maxBodyLen = rows.reduce((m, r) => {
          const v = r[c.key];
          if (v == null) return m;
          return Math.max(m, Math.min(String(v).length, 60));
        }, 0);
        return { wch: Math.min(48, Math.max(headerLen, maxBodyLen + 2, 10)) };
      });

      // Sheet view: hide gridlines, freeze first 5 rows
      ws["!sheetViews"] = [{ showGridLines: false, state: "frozen", ySplit: 5, topLeftCell: "A6" }];

      // Worksheet range
      ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(lastUsedRow, 4), c: lastColIdx } });

      XLSX.utils.book_append_sheet(wb, ws, title);
    }

    buildSheet({
      title: "Sale Comps",
      columns: saleColumns,
      rows: sales,
      totals: [
        { value: "" }, { value: "" }, { value: "" },
        { value: totalSaleSize || "", numFmt: '#,##0' },
        { value: totalSalePrice || "", numFmt: '"$"#,##0' },
        { value: avgPsf ? Math.round(avgPsf) : "", numFmt: '"$"#,##0', accent: true },
        { value: avgCap ? Number(avgCap.toFixed(2)) : "", numFmt: '0.00"%"', accent: true },
        { value: "" }, { value: "" }, { value: "" },
      ],
    });

    buildSheet({
      title: "Lease Comps",
      columns: leaseColumns,
      rows: leases,
      totals: [
        { value: "" }, { value: "" }, { value: "" }, { value: "" },
        { value: totalLeaseSize || "", numFmt: '#,##0' },
        { value: avgLeaseRate ? Number(avgLeaseRate.toFixed(2)) : "", numFmt: '"$"#,##0.00', accent: true },
        { value: "" }, { value: "" }, { value: "" },
      ],
    });

    buildSheet({
      title: "Rent Roll",
      columns: rrColumns,
      rows: allRR,
      totals: [
        { value: "TOTALS" },
        { value: "" },
        { value: rrTotalSf || "", numFmt: '#,##0' },
        { value: inPlaceWtd ? Number(inPlaceWtd.toFixed(2)) : "", numFmt: '"$"#,##0.00', accent: true },
        { value: "" }, { value: "" }, { value: "" },
        { value: avgWalt ? Number(avgWalt.toFixed(1)) : "", numFmt: '0.0"y"' },
        { value: "" },
      ],
    });

    const safeName = dealLabel.replace(/[^\w\-]+/g, "_").slice(0, 60);
    const fileStamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `${safeName}_comp_tables_${fileStamp}.xlsx`);
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 12, fontFamily: "'Montserrat', sans-serif" }}>
      {/* ── Action bar ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 10 }}>
        <button
          onClick={() => {
            try { exportXLSX(); }
            catch (e) { console.error(e); window.alert(`Export failed: ${e.message || e}`); }
          }}
          disabled={sales.length === 0 && leases.length === 0 && allRR.length === 0}
          style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 5, border: `1px solid ${S.border}`, background: S.card, color: S.text, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}
        >
          📥 Export XLSX
        </button>
        <button
          onClick={() => setEditMode(v => !v)}
          style={{ fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 5, border: `1px solid ${editMode ? S.red : S.border}`, background: editMode ? S.red : S.card, color: editMode ? "#fff" : S.text, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}
        >
          {editMode ? "✓ Done editing" : "✏ Edit"}
        </button>
        {busy && <span style={{ alignSelf: "center", fontSize: 10, color: S.muted }}>saving…</span>}
      </div>

      {/* Header card */}
      <div style={{ marginBottom: 12, padding: 10, border: `1px solid ${S.border}`, borderLeft: `3px solid #ffffff`, borderRadius: 8, background: S.card }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: S.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Deal Comp Tables</div>
        <div style={{ fontSize: 12, fontWeight: 800, color: S.text, marginBottom: 6 }}>
          {targetIds.length === 1
            ? (focusedDealsData.get(targetIds[0])?.name || "deal")
            : `${targetIds.length} deals`}
        </div>
        {focusedDealIds.length > 1 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <button
              onClick={() => setDrawerDealId(null)}
              style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, border: `1px solid ${S.border}`, background: !drawerDealId ? S.red : "transparent", color: !drawerDealId ? "#fff" : S.muted, cursor: "pointer" }}
            >All</button>
            {focusedDealIds.map(did => {
              const name = focusedDealsData.get(did)?.name || "deal";
              const active = drawerDealId === did;
              return (
                <button key={did}
                  onClick={() => setDrawerDealId(did)}
                  style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, border: `1px solid ${S.border}`, background: active ? S.red : "transparent", color: active ? "#fff" : S.text, cursor: "pointer", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >{name}</button>
              );
            })}
          </div>
        )}
      </div>

      {/* Mark-to-market callout */}
      {mtm != null && (
        <div style={{ marginBottom: 12, padding: 8, border: `1px solid ${S.border}`, borderRadius: 8, background: S.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 800, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Mark-to-Market</div>
            <div style={{ fontSize: 9, color: S.muted, marginTop: 2 }}>Market lease vs in-place rent roll</div>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: mtm > 0 ? "#4f9d76" : mtm < 0 ? S.red : S.text }}>
            {mtm > 0 ? "+" : ""}{mtm.toFixed(1)}%
          </div>
        </div>
      )}

      <CompTable
        title="Sale Comps"
        count={sales.length}
        columns={saleColumns}
        rows={sales}
        S={S}
        minWidth={1020}
        emptyMsg="No sale comps assigned to this deal."
        editMode={editMode}
        onCellSave={saveCompCell}
        footer={[
          { label: "# Comps",   value: sales.length },
          { label: "Avg $/SF",  value: avgPsf ? `$${avgPsf.toFixed(0)}` : "—", accent: !!avgPsf },
          { label: "Avg Cap",   value: fmtPct(avgCap), accent: !!avgCap },
          { label: "Med Cap",   value: fmtPct(medCap) },
          { label: "Total SF",  value: totalSaleSize ? totalSaleSize.toLocaleString() : "—" },
          { label: "Total $",   value: fmtMoney(totalSalePrice) },
        ]}
      />

      <CompTable
        title="Lease Comps"
        count={leases.length}
        columns={leaseColumns}
        rows={leases}
        S={S}
        minWidth={980}
        emptyMsg="No lease comps assigned to this deal."
        editMode={editMode}
        onCellSave={saveCompCell}
        footer={[
          { label: "# Comps",  value: leases.length },
          { label: "Avg Rate", value: avgLeaseRate ? `$${avgLeaseRate.toFixed(2)}` : "—", accent: !!avgLeaseRate },
          { label: "Med Rate", value: medLeaseRate ? `$${medLeaseRate.toFixed(2)}` : "—" },
          { label: "Total SF", value: totalLeaseSize ? totalLeaseSize.toLocaleString() : "—" },
        ]}
      />

      <CompTable
        title="Rent Roll (In-Place)"
        count={allRR.length}
        columns={rrColumns}
        rows={allRR}
        S={S}
        minWidth={820}
        emptyMsg="No rent roll rows saved on this deal."
        editMode={editMode}
        onCellSave={saveRentRollCell}
        footer={[
          { label: "# Tenants",     value: allRR.length },
          { label: "Total SF",      value: rrTotalSf ? rrTotalSf.toLocaleString() : "—" },
          { label: "Wtd Avg Rate",  value: inPlaceWtd ? `$${inPlaceWtd.toFixed(2)}` : "—", accent: !!inPlaceWtd },
          { label: "Avg WALT",      value: avgWalt ? `${avgWalt.toFixed(1)}y` : "—" },
        ]}
      />
    </div>
  );
}
